<template>
  <button
    @click="close(true)"
    class="flex items-center justify-center px-3.5 py-2.5 ml-2 min-h-[40px] text-sm font-semibold text-white whitespace-nowrap bg-[#386f99] rounded-xl border border-transparent shadow-sm shadow-indigo-200 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 active:scale-95"
  >
    Export CSV
    <DocumentTextIcon class="w-4 h-4 ms-2.5 stroke-[2]" />
  </button>

  <Teleport to="body">
    <div
      class="fixed inset-0 z-[123456789090] flex items-center justify-center p-4 sm:p-6"
      v-if="isVisibleModal"
    >
      <div
        class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
        @click="isVisibleModal = false"
      ></div>

      <div
        class="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-300"
      >
        <div
          class="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50"
        >
          <div class="flex items-center gap-4">
            <div
              class="w-10 h-10 rounded-xl bg-[#386f99] flex items-center justify-center text-white shadow-lg shadow-indigo-100"
            >
              <DocumentTextIcon class="w-6 h-6" />
            </div>
            <h2 class="text-xl font-bold text-slate-800">
              Export {{ resourceName }}
            </h2>
          </div>
          <button
            class="p-2 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
            @click="isVisibleModal = false"
          >
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>

        <div class="p-6 overflow-y-auto custom-scrollbar space-y-8">
          <div class="space-y-3">
            <h3
              class="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wider"
            >
              <DocumentIcon class="w-5 h-5 text-slate-500" /> File Format
            </h3>
            <div class="flex gap-4">
              <label
                class="flex-1 flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer hover:border-indigo-300 transition-all group"
              >
                <input
                  type="radio"
                  v-model="fileFormat"
                  value="csv"
                  class="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-slate-300"
                />
                <span class="font-medium text-slate-700">CSV</span>
              </label>
            </div>
          </div>

          <div
            class="space-y-3"
            v-if="exportItems === 'filtered' && activeFilters.length"
          >
            <h3
              class="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wider"
            >
              <FunnelIcon class="w-5 h-5 text-slate-500" /> Active Filters
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div
                v-for="filter in activeFilters"
                :key="filter.label"
                class="flex items-center justify-between p-3 rounded-xl border border-indigo-100 bg-indigo-50/50 text-sm"
              >
                <strong class="text-black">{{ filter.label }}:</strong>
                <span class="text-slate-400 truncate ml-2 font-medium">{{
                  formatValue(filter.value)
                }}</span>
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <h3
              class="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wider"
            >
              <Squares2X2Icon class="w-5 h-5 text-slate-500" /> Columns
            </h3>
            <div class="flex flex-wrap gap-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  v-model="columnMode"
                  value="all"
                  class="text-indigo-600"
                />
                <span class="text-slate-700 font-medium text-sm"
                  >All columns</span
                >
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  v-model="columnMode"
                  value="custom"
                  class="text-indigo-600"
                />
                <span class="text-slate-700 font-medium text-sm"
                  >Custom selection</span
                >
              </label>
            </div>

            <div
              v-if="columnMode === 'custom'"
              class="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-4"
            >
              <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                <label
                  v-for="column in columns"
                  :key="column.slug"
                  class="relative flex items-center p-3 rounded-xl border transition-all cursor-pointer overflow-hidden bg-white border-slate-200 hover:border-slate-300 has-[:checked]:bg-indigo-50/50 has-[:checked]:border-indigo-500 has-[:checked]:shadow-sm after:content-[''] after:absolute after:inset-x-0 after:top-0 after:h-[3.5px] after:bg-blue-600/40 after:opacity-0 has-[:checked]:after:opacity-100 transition-all duration-300"
                >
                  <div class="relative flex items-center">
                    <input
                      type="checkbox"
                      :checked="selectedColumns[column.slug]"
                      @change="toggleColumn(column.slug)"
                      class="peer h-5 w-5 cursor-pointer rounded border border-slate-300 bg-white transition-all checked:bg-[#386f99] checked:border-indigo-600 /* Piccola ombra interna per il checkbox */ focus:ring-2 focus:ring-indigo-500/20"
                    />
                    <CheckIcon
                      class="absolute w-3.5 h-3.5 text-white left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100 pointer-events-none"
                    />
                  </div>

                  <span
                    class="ml-3 text-sm font-bold truncate transition-colors"
                    :class="
                      selectedColumns[column.slug]
                        ? 'text-indigo-900'
                        : 'text-slate-600'
                    "
                  >
                    {{ column.label || column.slug }}
                  </span>
                </label>
              </div>
              <div
                v-if="!hasSelectedColumns"
                class="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm font-medium"
              >
                <ExclamationCircleIcon class="w-4 h-4" /> Please select at least
                one column
              </div>
            </div>
          </div>

          <div v-if="fileFormat === 'csv'" class="space-y-4">
            <h3
              class="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wider"
            >
              <Cog6ToothIcon class="w-5 h-5 text-slate-500" /> CSV Options
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div class="space-y-2 flex flex-col items-stretch justify-between">
                <label class="text-xs font-bold text-slate-500 uppercase"
                  >Predefined Limit</label
                >
                <div class="flex gap-2">
                  <button
                    @click="csvLimitRow = '300000'"
                    class="flex-1 px-3 py-2 min-h-[42px] rounded-lg text-xs font-bold transition-all border"
                    :class="
                      csvLimitRow == 300000
                        ? 'bg-[#386f99] text-white border-indigo-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    "
                  >
                    300k Rows
                  </button>
                  <button
                    @click="csvLimitRow = '1000000'"
                    class="flex-1 px-3 py-2 min-h-[42px] rounded-lg text-xs font-bold transition-all border"
                    :class="
                      csvLimitRow == 1000000
                        ? 'bg-[#386f99] text-white border-indigo-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    "
                  >
                    1MLN Rows
                  </button>
                </div>
              </div>
              <div class="space-y-2 flex flex-col items-stretch justify-between">
                <label class="text-xs font-bold text-slate-500 uppercase"
                  >Custom Limit</label
                >
                <input
                  type="number"
                  v-model.number="csvLimitRow"
                  :min="1"
                  :max="1000000"
                  @input="validateCsvLimit"
                  placeholder="Max 1,000,000"
                  class="w-full px-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-slate-700 bg-slate-50/50"
                />
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <h3
              class="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wider"
            >
              <InformationCircleIcon class="w-5 h-5 text-slate-500" /> Meta
              Information
            </h3>
            <div class="space-y-4">
              <div class="flex flex-col gap-1.5">
                <label
                  class="text-sm font-semibold text-slate-700"
                  for="export-title"
                >
                  Title <span class="text-red-500">*</span>
                  <span class="text-xs font-normal text-slate-400 ml-1"
                    >(Optional, default auto-generated)</span
                  >
                </label>
                <input
                  id="export-title"
                  v-model.trim="exportTitle"
                  type="text"
                  placeholder="e.g. Export n."
                  class="px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-slate-700"
                  :class="{ 'border-red-500 ring-red-500/10': titleError }"
                />
                <p
                  v-if="titleError"
                  class="text-xs font-medium text-red-500 mt-1"
                >
                  Export title is required.
                </p>
              </div>

              <div class="flex flex-col gap-1.5">
                <label
                  class="text-sm font-semibold text-slate-700"
                  for="export-description"
                  >Description</label
                >
                <textarea
                  id="export-description"
                  v-model.trim="exportDescription"
                  rows="3"
                  placeholder="What is this export for?"
                  class="px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-slate-700"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-end gap-3 p-4 bg-slate-50 border-t border-slate-100"
        >
          <button
            @click="closeModal"
            class="px-5 py-2.5 rounded-xl font-semibold text-slate-600 hover:bg-slate-200 transition-colors flex items-center gap-2"
          >
            <XMarkIcon class="w-4 h-4" /> Cancel
          </button>
          <button
            @click="exportData"
            :disabled="!canExport"
            class="px-6 py-2.5 rounded-xl font-semibold bg-[#386f99] text-white shadow-lg shadow-indigo-200 hover:bg-indigo-700 disabled:bg-slate-300 disabled:shadow-none disabled:cursor-not-allowed transition-all flex items-center gap-2 transform active:scale-95"
          >
            <ArrowDownTrayIcon class="w-4 h-4" /> Schedule Export
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <Toast
      v-if="toastVisible"
      :message="toastMessage"
      :type="toastType"
      :duration="toastDuration"
      @closed="toastVisible = false"
    />
  </Teleport>
</template>

<script>
import { postFetch } from "@/api/axiosClient";
import { formatString } from "@/helpers/date";
import Toast from "../Utility/Toast.vue";
import { mapGetters } from "vuex";
import {
  DocumentTextIcon,
  XMarkIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  Squares2X2Icon,
  CheckIcon,
  ExclamationCircleIcon,
  Cog6ToothIcon,
  FunnelIcon,
  InformationCircleIcon,
} from "@heroicons/vue/24/outline";

export default {
  name: "ExportDataAdvance",
  components: {
    DocumentTextIcon,
    XMarkIcon,
    DocumentIcon,
    ArrowDownTrayIcon,
    Squares2X2Icon,
    CheckIcon,
    ExclamationCircleIcon,
    Cog6ToothIcon,
    FunnelIcon,
    Toast,
    InformationCircleIcon,
  },
  props: {
    column: { type: Array, required: true },
    resourceName: { type: String, required: true },
    options: { type: Object, required: false },
  },
  data() {
    return {
      columns: [],
      selectedColumns: {},
      fileFormat: "csv",
      exportItems: "filtered",
      columnMode: "all",
      csvLimitRow: "300000",
      isVisibleModal: false,
      toastVisible: false,
      toastMessage: "",
      toastType: "info",
      toastDuration: 5000,
      exportTitle: "",
      exportDescription: "",
      titleError: false,
    };
  },
  watch: {
    isVisibleModal(newVal) {
      document.body.style.overflow = newVal ? "hidden" : "";
    },
  },
  computed: {
    ...mapGetters(["allowedSkins", "skinStorage"]),
    skinFilt() {
      const param = this.$route.params.skinFilt;
      if (param && param !== "all") return param;
      return this.skinStorage || "";
    },
    hasSelectedColumns() {
      if (this.columnMode === "all") return true;
      return Object.values(this.selectedColumns).some((selected) => selected);
    },
    canExport() {
      return this.hasSelectedColumns;
    },
    activeFilters() {
      if (this.exportItems !== "filtered" || !this.options) return [];
      return Object.entries(this.options)
        .filter(
          ([_, value]) =>
            value !== null &&
            value !== "" &&
            value !== undefined &&
            value !== "All",
        )
        .map(([key, value]) => ({
          property: key,
          value,
          label: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        }));
    },
  },
  methods: {
    validateCsvLimit() {
      if (this.csvLimitRow < 1) this.csvLimitRow = 1;
      else if (this.csvLimitRow > 1000000) this.csvLimitRow = 1000000;
    },
    toggleColumn(key) {
      this.selectedColumns[key] = !this.selectedColumns[key];
    },
    initializeSelectedColumns() {
      const newSelected = {};
      this.columns.forEach((column) => {
        newSelected[column.slug] = true;
      });
      this.selectedColumns = newSelected;
    },
    close(v = false) {
      this.isVisibleModal = v;
    },
    closeModal() {
      this.isVisibleModal = false;
      this.resetForm();
    },
    exportData() {
      this.titleError = false;
      if (!this.exportTitle) {
        const formattedDate = formatString(new Date().toString());
        this.exportTitle = `${this.resourceName}-export-${formattedDate.replace(
          /\s+/g,
          "-",
        )}`;
      }
      if (!this.canExport) return;

      const selectedColumnSlugs =
        this.columnMode === "custom"
          ? Object.entries(this.selectedColumns)
              .filter(([_, v]) => v)
              .map(([slug]) => slug)
          : this.columns.map((col) => col.slug);

      const transformedFilters = this.activeFilters.map((filter) => {
        if (filter.property === "player_id") {
          return { ...filter, property: "user_id", label: "User Id" };
        } else if (filter.property === "cashout_time") {
          return { ...filter, property: "updated_at", label: "Cashout Time" };
        }
        return filter;
      });

      const exportConfig = {
        title: this.exportTitle.replace(/\s+/g, "-"),
        description: this.exportDescription,
        fileFormat: this.fileFormat,
        selectedColumns: selectedColumnSlugs,
        csvLimitRow: parseInt(this.csvLimitRow),
        filters: transformedFilters,
        model: this.resourceName,
        skin_id: this.skinFilt ? (parseInt(this.skinFilt) || this.skinFilt) : undefined,
      };
      this.startExport(exportConfig);
    },
    showToast(message, type = "info", duration = 3000) {
      this.toastMessage = message;
      this.toastType = type;
      this.toastDuration = duration;
      this.toastVisible = true;
    },
    async startExport(exportFile) {
      try {
        const response = await postFetch("exports", exportFile);
        if (response && response.status === 200) {
          this.showToast("Export scheduled successfully!", "success", 5000);
          this.resetForm();
          this.isVisibleModal = false;
          setTimeout(() => {
            const lang = this.$route.params.lang || "en";
            const skin = this.skinFilt || this.$route.params.skinFilt || this.skinStorage;
            this.$router.push({
              name: "ExportPage",
              params: { lang, ...(skin ? { skinFilt: skin.toString() } : {}) },
            });
          }, 1000);
        } else {
          throw new Error(response?.error || "Unknown error");
        }
      } catch (error) {
        this.showToast(
          "Failed to save export: " + error.message,
          "error",
          4000,
        );
      }
    },
    formatValue(value) {
      if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
        return new Date(value).toLocaleString();
      }
      return value;
    },
    resetForm() {
      this.fileFormat = "csv";
      this.exportItems = "filtered";
      this.columnMode = "all";
      this.csvLimitRow = "300000";
      this.exportTitle = "";
      this.exportDescription = "";
      this.titleError = false;
      this.initializeSelectedColumns();
    },
  },
  mounted() {
    if (this.column?.length > 0) {
      this.columns = this.column;
      this.initializeSelectedColumns();
    }
  },
};
</script>
